import { format, parseISO } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';

/**
 * Format date according to locale, with optional timezone support
 */
export const formatDate = (
  date: string | Date,
  formatString: string = 'PP',
  locale: 'ar' | 'en' = 'ar',
  timeZone?: string
): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;

  if (!dateObj || isNaN(dateObj.getTime())) return '-';

  const localeObj = locale === 'ar' ? ar : enUS;

  if (timeZone) {
    const localeCode = locale === 'ar' ? 'ar-SY' : 'en-US';
    const d = dateObj instanceof Date ? dateObj : new Date(dateObj);
    const opts: Intl.DateTimeFormatOptions = {
      timeZone,
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: formatString.includes('a'),
    };
    const parts = new Intl.DateTimeFormat(localeCode, opts).formatToParts(d);
    const map: Record<string, string> = {};
    for (const p of parts) map[p.type] = p.value;
    const monthNum = new Intl.DateTimeFormat('en-US', { timeZone, month: 'numeric' }).format(d);
    const time = map.dayPeriod ? `${map.hour}:${map.minute} ${map.dayPeriod}` : `${map.hour}:${map.minute}`;
    return `${map.day} ${map.month} (${monthNum}) ${map.year} ${time}`;
  }

  return format(dateObj, formatString, { locale: localeObj });
};

/**
 * Format date relative to now (e.g., "2 hours ago")
 */
export const formatRelativeTime = (
  date: string | Date,
  locale: 'ar' | 'en' = 'ar'
): string => {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;

  if (!dateObj || isNaN(dateObj.getTime())) return '-';

  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);

  if (locale === 'ar') {
    if (diffInSeconds < 60) return 'منذ لحظات';
    if (diffInSeconds < 3600)
      return `منذ ${Math.floor(diffInSeconds / 60)} دقيقة`;
    if (diffInSeconds < 86400)
      return `منذ ${Math.floor(diffInSeconds / 3600)} ساعة`;
    if (diffInSeconds < 2592000)
      return `منذ ${Math.floor(diffInSeconds / 86400)} يوم`;
    return formatDate(dateObj, 'PP', 'ar');
  } else {
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600)
      return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 2592000)
      return `${Math.floor(diffInSeconds / 86400)} days ago`;
    return formatDate(dateObj, 'PP', 'en');
  }
};
